import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PerModal } from '../Modal'
import PerText from '../Text'
import PerFlexRow from '../FlexRow'
import { KudosType } from '../../../types'
import { KudosIconIndicator } from '../../../modules/kudos/components/KudosIconIndicator'
import TextArea from 'antd/lib/input/TextArea'
import { createAttentionPointInPublicProfile } from '../../../graphql/kudos/useKudosMutations'
import { getKudosColor } from '../../../shared/kudosUtil'
import { showNotification } from '../Notification/notification'

interface CreateAttentionPointModalProps {
  visible: boolean
  closeModal: () => void
  employeeId: string
  employeeFullName: string
  onAttentionPointCreated: () => void
}

export const CreateAttentionPointModalInPublicProfile: FC<CreateAttentionPointModalProps> = ({
  visible,
  closeModal,
  employeeId,
  employeeFullName,
  onAttentionPointCreated,
}) => {
  const { t } = useTranslation()

  const [description, setDescription] = useState<string>('')
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string>('')

  const handleCreate = async () => {
    if (!description.trim()) return

    setIsLoading(true)
    setErrorMessage('')

    const result = await createAttentionPointInPublicProfile(employeeId, description.trim())

    if (!!result.data && !result.errors) {
      onAttentionPointCreated()
      resetFormAndCloseModal()
      showNotification({
        type: 'success',
        message: t('attentionPoint.notifications.create.successTitle'),
        description: t('attentionPoint.notifications.create.successDescription'),
      })
    } else {
      const errorMessage = result.errors?.[0]?.extensions?.validationErrors?.[0]?.message || t('attentionPoint.createModal.errors.generic')
      setErrorMessage(errorMessage)
      setIsLoading(false)
    }
  }

  const resetFormAndCloseModal = () => {
    setDescription('')
    setErrorMessage('')
    setIsLoading(false)
    closeModal()
  }

  return (
    <PerModal
      title={{ heading: t('attentionPoint.createModal.title') }}
      size="large"
      centered
      visible={visible}
      onCancel={resetFormAndCloseModal}
      footer={{
        cancelBtn: {
          text: t('global.button.cancel'),
          onClick: resetFormAndCloseModal,
          icon: 'close_stroke',
          visible: !isLoading,
        },
        confirmBtn: {
          altText: t('attentionPoint.createModal.title'),
          onClick: handleCreate,
          disabled: !description.trim() || isLoading,
          isProcessing: { active: isLoading },
        },
      }}
    >
      <PerFlexRow direction="column">
        {/* Disclaimer */}
        <PerText fontSize="m" mb="m">
          {t('attentionPoint.createModal.disclaimer')}
        </PerText>

        {/* Attention Point Info */}
        <PerFlexRow align="middle" mb="m">
          <KudosIconIndicator kudosType={KudosType.Negative} />
          <PerFlexRow align="middle">
            <PerText fontSize="l" ml="xs">
              {t('attentionPoint.createModal.forEmployee')}
            </PerText>
            <PerText fontSize="l" fontWeight="bold" ml="xs" color={getKudosColor(KudosType.Negative)}>
              {employeeFullName}
            </PerText>
          </PerFlexRow>
        </PerFlexRow>

        {errorMessage && (
          <PerText color="error" fontSize="m" mb="m">
            {errorMessage}
          </PerText>
        )}

        {/* Description */}
        <PerFlexRow direction="column">
          <PerText fontSize="m" fontWeight="bold" mb="xxs">
            {t('attentionPoint.description')}
          </PerText>
          <TextArea
            rows={4}
            placeholder={t('attentionPoint.createModal.descriptionPlaceholder')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={1000}
          />
        </PerFlexRow>
      </PerFlexRow>
    </PerModal>
  )
}

export default CreateAttentionPointModalInPublicProfile
